import React, { useState, useEffect } from 'react';
import ReactDOM from 'react-dom';
import { motion, AnimatePresence, Variants } from 'framer-motion';
import { Share2, X, Copy, Download, Check, ExternalLink, MapPin, FileText, Link2 } from 'lucide-react';
import Image from 'next/image';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from './tooltip';
import { Switch } from './switch';
import { Label } from './label';
import { buildCityScopeUtmUrl } from '@/lib/analyticsAttribution';
import { trackCoreJourneyEvent } from '@/lib/coreJourneyAnalytics';
import { buildShareModalSummaryText } from '@/lib/shareModalCopy';

const Portal: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [mounted, setMounted] = useState(false);

  useEffect(() => {
    setMounted(true);
    return () => setMounted(false);
  }, []);

  return mounted
    ? ReactDOM.createPortal(children, document.body)
    : null;
};

interface ShareButtonProps {
  application: {
    reference: string;
    description: string;
    aiSummary?: string;
    location: string;
    status: string;
    decision_date?: string;
    application_type?: string;
  };
  documents?: Array<{
    thumbnail_url: string;
    document_url: string;
    document_type?: string;
  }>;
  // Optional override for the URL to share (e.g., modal deep link)
  shareUrl?: string;
  // External control — when provided the default trigger button is hidden
  isOpen?: boolean;
  onOpenChange?: (open: boolean) => void;
  hideDefaultTrigger?: boolean;
}

const ShareButton: React.FC<ShareButtonProps> = ({ application, documents = [], shareUrl, isOpen: externalIsOpen, onOpenChange, hideDefaultTrigger = false }) => {
  const [internalIsOpen, setInternalIsOpen] = useState(false);
  const isOpen = externalIsOpen !== undefined ? externalIsOpen : internalIsOpen;
  const setIsOpen = (v: boolean) => {
    setInternalIsOpen(v);
    onOpenChange?.(v);
  };
  const [useAiDescription, setUseAiDescription] = useState(true);
  const [copyingImage, setCopyingImage] = useState<string | null>(null);
  const [copiedImage, setCopiedImage] = useState<string | null>(null);
  const [copiedUrl, setCopiedUrl] = useState<string | null>(null);
  const [copiedShareLink, setCopiedShareLink] = useState(false);
  const [copiedSummary, setCopiedSummary] = useState(false);

  const siteUrl = 'https://www.city-scope.co.uk';
  const effectiveUrl = shareUrl || (typeof window !== 'undefined' ? window.location.href : siteUrl);
  const attributedShareUrl = buildCityScopeUtmUrl(effectiveUrl, {
    src: 'share_modal',
    utmSource: 'cityscope',
    utmMedium: 'copy_link',
    utmCampaign: 'planning_application',
    utmContent: 'share_modal',
    keepAbsolute: true,
  });
  const { reference, description, aiSummary, location } = application;
  const activeDescription = useAiDescription && aiSummary ? aiSummary : description;

  useEffect(() => {
    // Default to AI summary if it exists
    if (aiSummary && aiSummary.trim()) {
      setUseAiDescription(true);
    } else {
      setUseAiDescription(false);
    }
  }, [aiSummary, isOpen]);

  useEffect(() => {
    const handleEsc = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setIsOpen(false);
      }
    };
    window.addEventListener('keydown', handleEsc);

    return () => {
      window.removeEventListener('keydown', handleEsc);
    };
  }, []);

  const handleCopyImageUrl = async (imageUrl: string) => {
    try {
      await navigator.clipboard.writeText(imageUrl);
      trackCoreJourneyEvent('engagement', 'share_clicked', {
        source: 'share_modal',
        placement: 'document_image_url',
        entityType: 'planning_application',
        entityId: application.reference,
        status: 'copied_url',
      });
      setCopiedUrl(imageUrl);
      setTimeout(() => setCopiedUrl(null), 3000);
    } catch (err) {
      console.error('Failed to copy URL:', err);
      window.open(imageUrl, '_blank');
    }
  };

  const handleCopyShareLink = async () => {
    try {
      await navigator.clipboard.writeText(attributedShareUrl);
      trackCoreJourneyEvent('engagement', 'share_clicked', {
        source: 'share_modal',
        placement: 'primary_copy_link',
        entityType: 'planning_application',
        entityId: application.reference,
        status: 'copied_link',
      });
      setCopiedShareLink(true);
      setTimeout(() => setCopiedShareLink(false), 2200);
    } catch (err) {
      console.error('Failed to copy share link:', err);
      trackCoreJourneyEvent('engagement', 'share_clicked', {
        source: 'share_modal',
        placement: 'primary_copy_link',
        entityType: 'planning_application',
        entityId: application.reference,
        status: 'copy_failed',
      });
    }
  };

  const handleCopySummary = async () => {
    const summaryText = buildShareModalSummaryText({
      reference,
      description: activeDescription,
      location,
      url: attributedShareUrl,
    });

    try {
      await navigator.clipboard.writeText(summaryText);
      trackCoreJourneyEvent('engagement', 'share_clicked', {
        source: 'share_modal',
        placement: 'copy_summary',
        entityType: 'planning_application',
        entityId: application.reference,
        status: 'copied_summary',
      });
      setCopiedSummary(true);
      setTimeout(() => setCopiedSummary(false), 2200);
    } catch (err) {
      console.error('Failed to copy share summary:', err);
      trackCoreJourneyEvent('engagement', 'share_clicked', {
        source: 'share_modal',
        placement: 'copy_summary',
        entityType: 'planning_application',
        entityId: application.reference,
        status: 'copy_failed',
      });
    }
  };

  const handleCopyImage = async (imageUrl: string) => {
    setCopyingImage(imageUrl);
    
    if (!navigator.clipboard || !navigator.clipboard.write) {
      setCopyingImage(null);
      await handleCopyImageUrl(imageUrl);
      return;
    }

    try {
      const response = await fetch(imageUrl);
      
      if (!response.ok) {
        throw new Error('Fetch failed');
      }

      const blob = await response.blob();
      
      if (!blob.type.startsWith('image/')) {
        throw new Error('Not an image');
      }
      
      const clipboardItem = new ClipboardItem({
        [blob.type]: blob
      });

      await navigator.clipboard.write([clipboardItem]);
      trackCoreJourneyEvent('engagement', 'share_clicked', {
        source: 'share_modal',
        placement: 'document_image',
        entityType: 'planning_application',
        entityId: application.reference,
        status: 'copied_image',
      });
      
      setCopiedImage(imageUrl);
      setTimeout(() => setCopiedImage(null), 3000);
    } catch (err) {
      setCopyingImage(null);
      await handleCopyImageUrl(imageUrl);
    } finally {
      setCopyingImage(null);
    }
  };

  const handleDownloadImage = (imageUrl: string) => {
    trackCoreJourneyEvent('engagement', 'download_clicked', {
      source: 'share_modal',
      placement: 'document_image',
      entityType: 'planning_application',
      entityId: application.reference,
      status: 'download_started',
    });
    const link = document.createElement('a');
    link.href = imageUrl;
    link.download = `planning-doc-${application.reference}.jpg`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };
  
  // --- Create formatted text for sharing ---
  const title = reference;
  const locationInfo = `📍 ${location}`;

  // For platforms like Twitter with strict character limits
  const createTwitterText = () => {
    const urlLength = 23; // Twitter's t.co link length
    const overhead = title.length + locationInfo.length + urlLength + 6; // Account for newlines, spaces, emojis
    const maxDescLength = 280 - overhead;
    
    const truncatedDesc = activeDescription.length > maxDescLength
      ? `${activeDescription.substring(0, maxDescLength - 3)}...`
      : activeDescription;
    
    return [ title, `📝 ${truncatedDesc}`, locationInfo ].join('\n\n');
  };

  // For other platforms
  const createDefaultText = () => {
    return [ title, `📝 ${activeDescription}`, locationInfo ].join('\n\n');
  };

  const twitterText = createTwitterText();
  const defaultText = createDefaultText();
  // ---

  const socialPlatforms = [
    {
      name: 'Twitter',
      icon: '𝕏',
      url: `https://twitter.com/intent/tweet?text=${encodeURIComponent(twitterText)}&url=${encodeURIComponent(effectiveUrl)}`
    },
    {
      name: 'Bluesky',
      icon: '🦋',
      url: `https://bsky.app/intent/compose?text=${encodeURIComponent(defaultText + '\n\n' + effectiveUrl)}`
    },
    {
      name: 'Reddit',
      icon: '📱',
      url: `https://reddit.com/submit?title=${encodeURIComponent(title)}&text=${encodeURIComponent(defaultText + '\n\n' + effectiveUrl)}`
    },
    {
      name: 'LinkedIn',
      icon: '💼',
      url: `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(effectiveUrl)}&title=${encodeURIComponent(title)}&summary=${encodeURIComponent(activeDescription)}`
    },
    {
      name: 'Facebook',
      icon: '📘',
      url: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(effectiveUrl)}&quote=${encodeURIComponent(defaultText)}`
    },
    {
      name: 'WhatsApp',
      icon: '💬',
      url: `https://wa.me/?text=${encodeURIComponent(defaultText + '\n\n' + effectiveUrl)}`
    },
    {
      name: 'Email',
      icon: '✉️',
      url: `mailto:?subject=${encodeURIComponent(title)}&body=${encodeURIComponent(defaultText + '\n\n' + effectiveUrl)}`
    }
  ];

  // Utility component for an icon-only social button with tooltip
  const SocialButton: React.FC<{ name: string; icon: string; url: string }> = ({ name, icon, url }) => (
    <Tooltip>
      <TooltipTrigger asChild>
        <motion.a
          href={url}
          target="_blank"
          rel="noopener noreferrer"
          data-analytics-event="share_social_clicked"
          data-analytics-label={`share_${name.toLowerCase()}`}
          onClick={() => {
            trackCoreJourneyEvent('engagement', 'share_clicked', {
              source: 'share_modal',
              placement: name,
              entityType: 'planning_application',
              entityId: application.reference,
              status: 'social_clicked',
            });
          }}
          className="w-11 h-11 flex items-center justify-center rounded-[var(--radius-sm)] border border-[var(--c-border)] bg-[var(--c-bg)] text-[var(--c-text-primary)] hover:bg-[var(--c-surface-hover)] hover:border-[var(--c-accent)]/50 hover:text-[var(--c-accent)] transition-all duration-200"
          whileHover={{ scale: 1.15 }}
          whileTap={{ scale: 0.9 }}
        >
          <span className="text-lg leading-none">{icon}</span>
        </motion.a>
      </TooltipTrigger>
      <TooltipContent><p>{name}</p></TooltipContent>
    </Tooltip>
  );

  const backdropVariants = {
    hidden: { opacity: 0 },
    visible: { opacity: 1, transition: { duration: 0.2, ease: "easeOut" as const } },
    exit: { opacity: 0, transition: { duration: 0.2, ease: "easeIn" as const } }
  };

  const modalVariants: Variants = {
    hidden: { opacity: 0, scale: 0.95, y: 40 },
    visible: { 
      opacity: 1, 
      scale: 1, 
      y: 0,
      transition: { type: 'spring', stiffness: 400, damping: 30 }
    },
    exit: { 
      opacity: 0, 
      scale: 0.95, 
      y: 40,
      transition: { duration: 0.2, ease: "easeIn" as const }
    }
  };

  const staggerContainer = {
    visible: {
      transition: { staggerChildren: 0.05, delayChildren: 0.1 }
    }
  };

  const staggerItem = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0, transition: { duration: 0.3, ease: "easeOut" as const } }
  };

  return (
    <>
      {!hideDefaultTrigger && (
        <motion.button
          onClick={(e) => {
            e.stopPropagation();
            setIsOpen(true);
          }}
          className="flex h-11 w-11 items-center justify-center rounded-full border border-[var(--c-border)]/50 bg-[var(--c-surface)]/90 p-0 text-[var(--c-text-secondary)] shadow-sm backdrop-blur-md transition-all duration-200 hover:border-[var(--c-accent)]/50 hover:bg-[var(--c-surface-hover)] hover:text-[var(--c-accent)] hover:shadow-md sm:h-auto sm:w-auto sm:p-2"
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.95 }}
          aria-label="Share planning application"
        >
          <Share2 size={16} />
        </motion.button>
      )}

      <AnimatePresence>
        {isOpen && (
          <Portal>
            <TooltipProvider>
              <motion.div
                variants={backdropVariants}
                initial="hidden"
                animate="visible"
                exit="exit"
                className="planning-register fixed inset-0 bg-black/60 flex items-center justify-center p-4 z-[11000]"
                onClick={() => setIsOpen(false)}
              >
                <motion.div
                  variants={modalVariants}
                  className="bg-[var(--c-surface)] border border-[var(--c-border)] rounded-[var(--radius-lg)] shadow-2xl max-w-2xl w-full max-h-[90vh] flex flex-col"
                  style={{ 
                    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.8), 0 0 0 1px var(--c-border)'
                  }}
                  onClick={(e) => e.stopPropagation()}
                >
                  {/* Header */}
                  <div className="flex items-center justify-between p-6 border-b border-[var(--c-border)]/50 bg-[var(--c-bg)]/30 flex-shrink-0">
                    <div className="flex items-center gap-4">
                      <motion.div 
                        className="p-3 rounded-[var(--radius-md)] bg-[var(--c-accent-muted)] border border-[var(--c-accent)]/20"
                        whileHover={{ scale: 1.05, rotate: 5 }}
                        transition={{ type: "spring", stiffness: 400 }}
                      >
                        <Share2 size={20} className="text-[var(--c-accent)]" />
                      </motion.div>
                      <div>
                        <h3 className="text-xl font-bold text-[var(--c-text-primary)]">
                          Share Planning Application
                        </h3>
                        <p className="text-sm text-[var(--c-text-secondary)]">Share this application or copy images</p>
                      </div>
                    </div>
                    <motion.button
                      onClick={() => setIsOpen(false)}
                      className="p-2 rounded-full hover:bg-[var(--c-surface-hover)] text-[var(--c-text-secondary)] hover:text-[var(--c-text-primary)] transition-all duration-200 border border-[var(--c-border)] hover:border-[var(--c-accent)]/50"
                      whileHover={{ scale: 1.1, rotate: 90 }}
                      whileTap={{ scale: 0.9 }}
                      aria-label="Close"
                    >
                      <X size={18} />
                    </motion.button>
                  </div>

                  <motion.div 
                    className="p-6 space-y-6 overflow-y-auto"
                    variants={staggerContainer}
                    initial="hidden"
                    animate="visible"
                    exit="hidden"
                  >
                    {/* Application Preview */}
                    <motion.div 
                      className="p-5 bg-[var(--c-bg)] rounded-[var(--radius-md)] border border-[var(--c-border)]"
                      variants={staggerItem}
                    >
                      <div className="flex items-start gap-4">
                        <div className="p-2.5 rounded-[var(--radius-sm)] bg-[var(--c-accent-muted)] mt-1">
                          <FileText size={18} className="text-[var(--c-accent)]" />
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex justify-between items-start mb-2">
                            <h4 className="font-bold text-[var(--c-text-primary)] text-lg pr-2">{application.reference}</h4>
                            {useAiDescription && aiSummary && (
                              <motion.div 
                                initial={{opacity:0, scale:0.5}} 
                                animate={{opacity:1, scale:1}} 
                                className="text-xs font-bold text-[var(--c-accent)] bg-[var(--c-accent-muted)] px-2 py-1 rounded-full leading-none flex-shrink-0"
                              >
                                AI
                              </motion.div>
                            )}
                          </div>
                          <p className="text-sm text-[var(--c-text-secondary)] mb-4 leading-relaxed">{activeDescription}</p>
                          
                          <div className="flex flex-wrap gap-x-6 gap-y-3 text-sm">
                            <div className="flex items-center gap-2.5">
                              <MapPin size={14} className="text-[var(--c-accent)] flex-shrink-0" />
                              <span className="text-[var(--c-text-primary)] font-medium">{application.location}</span>
                            </div>
                            <div className="flex items-center gap-2.5">
                              <div className={`w-2.5 h-2.5 rounded-full flex-shrink-0 ${
                                application.status === 'Approved' ? 'bg-green-500' :
                                application.status === 'Refused' ? 'bg-red-500' :
                                'bg-yellow-500'
                              }`} />
                              <span className="text-[var(--c-text-secondary)] font-medium">{application.status}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    </motion.div>

                    {/* AI Summary Toggle */}
                    {aiSummary && aiSummary.trim() && (
                      <motion.div variants={staggerItem}>
                        <div className="flex items-center justify-between p-3.5 bg-[var(--c-bg)] rounded-[var(--radius-md)] border border-[var(--c-border)]">
                          <Label htmlFor="ai-switch" className="flex flex-col gap-1 cursor-pointer">
                            <span className="font-semibold text-[var(--c-text-primary)]">Use AI Summary</span>
                            <span className="text-xs text-[var(--c-text-secondary)] pr-4">Switch to the AI-generated description for sharing.</span>
                          </Label>
                          <Switch
                            id="ai-switch"
                            checked={useAiDescription}
                            onCheckedChange={setUseAiDescription}
                          />
                        </div>
                      </motion.div>
                    )}

                    {/* Primary copy-link sharing */}
                    <motion.div variants={staggerItem}>
                      <h4 className="text-base font-bold text-[var(--c-text-primary)] mb-4 flex items-center gap-3">
                        <div className="w-1 h-5 bg-[var(--c-accent)] rounded-full" />
                        Copy Share Link
                      </h4>
                      <div className="rounded-[var(--radius-md)] border border-[var(--c-border)] bg-[var(--c-bg)] p-4">
                        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
                          <div className="flex min-w-0 flex-1 items-center gap-2 rounded-[var(--radius-sm)] border border-[var(--c-border)] bg-[var(--c-surface)] px-3 py-2 text-sm text-[var(--c-text-secondary)]">
                            <Link2 size={16} className="flex-shrink-0 text-[var(--c-accent)]" />
                            <span className="min-w-0 truncate">{attributedShareUrl}</span>
                          </div>
                          <motion.button
                            type="button"
                            onClick={handleCopyShareLink}
                            className="inline-flex h-10 items-center justify-center gap-2 rounded-[var(--radius-sm)] bg-[var(--c-accent)] px-4 text-sm font-semibold text-[var(--c-accent-contrast,#111)] transition hover:brightness-105"
                            whileHover={{ scale: 1.02 }}
                            whileTap={{ scale: 0.98 }}
                          >
                            {copiedShareLink ? <Check size={16} /> : <Copy size={16} />}
                            {copiedShareLink ? 'Copied' : 'Copy link'}
                          </motion.button>
                        </div>
                        <div className="mt-3 flex flex-wrap gap-2">
                          <button
                            type="button"
                            onClick={handleCopySummary}
                            className="inline-flex items-center gap-2 rounded-[var(--radius-sm)] border border-[var(--c-border)] px-3 py-2 text-xs font-medium text-[var(--c-text-secondary)] transition hover:border-[var(--c-accent)]/40 hover:text-[var(--c-accent)]"
                          >
                            {copiedSummary ? <Check size={14} /> : <Copy size={14} />}
                            {copiedSummary ? 'Summary copied' : 'Copy summary'}
                          </button>
                        </div>
                      </div>
                    </motion.div>

                    {/* Document Images */}
                    {documents.length > 0 && (
                      <motion.div variants={staggerItem}>
                        <h4 className="text-base font-bold text-[var(--c-text-primary)] mb-4 flex items-center gap-3">
                          <div className="w-1 h-5 bg-[var(--c-accent)] rounded-full" />
                          Copy Application Images
                        </h4>
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                          {documents.slice(0, 4).map((doc, index) => (
                            <motion.div
                              key={index}
                              className="group relative aspect-video bg-[var(--c-bg)] rounded-[var(--radius-md)] overflow-hidden border border-[var(--c-border)] hover:border-[var(--c-accent)]/50 transition-all duration-300"
                              whileHover={{ y: -3 }}
                            >
                              <Image
                                src={doc.thumbnail_url || '/placeholder-image.jpg'}
                                alt={`Document ${index + 1}`}
                                fill
                                unoptimized
                                sizes="(max-width: 640px) 100vw, 50vw"
                                className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
                              />
                              <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent transition-opacity duration-300" />
                              
                              <div className="absolute inset-0 flex items-center justify-center p-3 opacity-0 group-hover:opacity-100 transition-all duration-300 transform scale-90 group-hover:scale-100">
                                <div className="flex items-center gap-3 bg-[var(--c-surface)]/80 backdrop-blur-sm p-2 rounded-[var(--radius-md)] border border-[var(--c-border)]">
                                  {/* Smart Copy */}
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <motion.button
                                        onClick={() => handleCopyImage(doc.thumbnail_url)}
                                        disabled={copyingImage === doc.thumbnail_url}
                                        className="flex items-center justify-center p-2.5 rounded-[var(--radius-sm)] bg-[var(--c-surface)] border border-[var(--c-border)] text-[var(--c-text-primary)] hover:bg-[var(--c-surface-hover)] hover:border-[var(--c-accent)]/50 hover:text-[var(--c-accent)] transition-all duration-200 disabled:opacity-50"
                                        whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                                      >
                                        {copyingImage === doc.thumbnail_url ? <div className="w-4 h-4 border-2 border-[var(--c-text-secondary)] border-t-transparent rounded-full animate-spin" />
                                        : copiedImage === doc.thumbnail_url ? <Check size={16} className="text-green-500" />
                                        : copiedUrl === doc.thumbnail_url ? <Check size={16} className="text-[var(--c-accent)]" />
                                        : <Copy size={16} />}
                                      </motion.button>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      {copiedImage === doc.thumbnail_url ? <p>Copied!</p>
                                      : copiedUrl === doc.thumbnail_url ? <p>URL Copied!</p>
                                      : <p>Smart Copy</p>}
                                    </TooltipContent>
                                  </Tooltip>
                                  
                                  {/* Copy URL */}
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <motion.button
                                        onClick={() => handleCopyImageUrl(doc.thumbnail_url)}
                                        className="flex items-center justify-center p-2.5 rounded-[var(--radius-sm)] bg-[var(--c-accent-muted)] border border-[var(--c-accent)]/30 text-[var(--c-accent)] hover:bg-[var(--c-accent)] hover:text-[var(--c-surface)] transition-all duration-200"
                                        whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                                      >
                                        <ExternalLink size={16} />
                                      </motion.button>
                                    </TooltipTrigger>
                                    <TooltipContent><p>Copy URL</p></TooltipContent>
                                  </Tooltip>
                                  
                                  {/* Download */}
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <motion.button
                                        onClick={() => handleDownloadImage(doc.thumbnail_url)}
                                        className="flex items-center justify-center p-2.5 rounded-[var(--radius-sm)] bg-[var(--c-surface)] border border-[var(--c-border)] text-[var(--c-text-primary)] hover:bg-[var(--c-surface-hover)] hover:border-[var(--c-accent)]/50 hover:text-[var(--c-accent)] transition-all duration-200"
                                        whileHover={{ scale: 1.1 }} whileTap={{ scale: 0.9 }}
                                      >
                                        <Download size={16} />
                                      </motion.button>
                                    </TooltipTrigger>
                                    <TooltipContent><p>Download Image</p></TooltipContent>
                                  </Tooltip>
                                </div>
                              </div>
                            </motion.div>
                          ))}
                        </div>
                      </motion.div>
                    )}
                  </motion.div>
                </motion.div>
              </motion.div>
            </TooltipProvider>
          </Portal>
        )}
      </AnimatePresence>
    </>
  );
};

export default ShareButton; 
